import { useMutation } from '@tanstack/react-query';
import { Form, Modal, Space, Switch, TableProps } from 'antd';
import { GroupRoleApis } from 'apis/GroupRoleApis';
import { EditIcon } from 'assets/svg';
import { ButtonAction, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { groupRewardOptions } from 'utils/constants/constant';
import {
  formatDate,
  generateUUID,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  onSave: () => void;
  onCancel: () => void;
  onEdit: (data: any) => void;
  indexHandle: number;
  // setIndexHandle: (value: number) => void;
  // isAdd: boolean;
  form: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const { SaveTwoTone, CloseCircleTwoTone } = icons;
const TableGroupRole = forwardRef((props: IProps, ref: any) => {
  const {
    data = {},
    loading,
    refetch,
    onSave,
    onCancel,
    onEdit,
    indexHandle,
    // isAdd,
    form,
  } = props;

  const { user } = useUser();
  const [status, setStatus] = useState<any>();
  const [optionsPosition, setOptionsPosition] =
    useState<ILazySelectData>(initLazySelectData);

  const {
    mutate: mutateUpdateGroupRoleManagementStatus,
    isPending: isPendingUpdateGroupRoleManagementStatus,
  } = useMutation({
    mutationFn: GroupRoleApis.updateGroupRoleManagementStatus,
    onSuccess: () => {
      toastSuccess('Cập nhật trạng thái thành công');
      refetch();
      setStatus(null);
    },
  });

  const {
    mutate: getComboboxJobTitle,
    isPending: isPendingGetComboboxJobTitle,
  } = useMutation({
    mutationFn: GroupRoleApis.getComboboxJobTitleNew,
    onSuccess: (res) => {
      const data = res?.data?.getGroupRoleManagement?.items || [];

      if (data.length === 0) return;

      const processedData = data.reduce((acc: any, item: any) => {
        const exist = acc.find((i: any) => i.value === item.jobTitle);
        if (!exist) {
          const label = item.jobTitleName;

          acc.push({
            label,
            value: item.jobTitle,
          });
        }
        return acc;
      }, []);

      setOptionsPosition({
        options: processedData,
        totalItems: res.data?.getGroupRoleManagement.pageInfo?.totalItems,
        hasNextPage: res.data?.getGroupRoleManagement.pageInfo?.hasNextPage,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handlePositionSelectScrollChange = useCallback(
    (value: ILazySelectParams) => {
      getComboboxJobTitle({
        skip: 0,
        take: value.pageSize,
        jobTitle: value.value,
      });
    },
    [getComboboxJobTitle],
  );

  const handlePositionSelectChange = (value: any) => {
    if (value) {
      const jobTitle = optionsPosition.options.find(
        (item: any) => item.value === value,
      );

      form.setFieldsValue({
        jobTitleName: jobTitle?.label,
      });
    } else {
      form.setFieldsValue({
        jobTitleName: '',
      });
    }
  };

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      width: 50,
      align: 'center',
      render: (_, __, index) =>
        data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
        index +
        1,
    },
    {
      title: 'Mã thưởng DMS',
      width: 130,
      dataIndex: ['rewardId'],
    },
    {
      title: 'Mã chức vụ',
      width: 120,
      dataIndex: 'jobTitle',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'jobTitle'} className="m-0">
              <LazySelectForm
                fieldNames={{
                  label: 'value',
                }}
                customeSize={20}
                data={optionsPosition}
                loading={isPendingGetComboboxJobTitle}
                placeholder="Chọn chức vụ"
                onSelectScrollChange={handlePositionSelectScrollChange}
                onChange={handlePositionSelectChange}
                size="middle"
              />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Chức vụ',
      width: 150,
      dataIndex: ['jobTitleName'],
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name="jobTitleName" className="m-0">
              <InputForm placeholder="Chức vụ" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Chi nhánh',
      width: 110,
      dataIndex: 'plantId',
    },
    {
      title: 'Mã đơn vị cấp 1',
      width: 130,
      dataIndex: 'unitId1',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitId1'} className="m-0">
              <InputForm placeholder="Nhập mã đơn vị cấp 1" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Đơn vị cấp 1',
      width: 150,
      dataIndex: 'unitName1',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitName1'} className="m-0">
              <InputForm placeholder="Nhập đơn vị cấp 1" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Mã đơn vị cấp 2',
      width: 130,
      dataIndex: 'unitId2',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitId2'} className="m-0">
              <InputForm placeholder="Nhập mã đơn vị cấp 2" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Đơn vị cấp 2',
      width: 150,
      dataIndex: 'unitName2',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitName2'} className="m-0">
              <InputForm placeholder="Nhập đơn vị cấp 2" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Mã đơn vị cấp 3',
      width: 130,
      dataIndex: 'unitId3',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitId3'} className="m-0">
              <InputForm placeholder="Nhập mã đơn vị cấp 3" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Đơn vị cấp 3',
      width: 150,
      dataIndex: 'unitName3',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitName3'} className="m-0">
              <InputForm placeholder="Nhập đơn vị cấp 3" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Mã đơn vị cấp 4',
      width: 130,
      dataIndex: 'unitId4',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitId4'} className="m-0">
              <InputForm placeholder="Nhập mã đơn vị cấp 4" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Đơn vị cấp 4',
      width: 150,
      dataIndex: 'unitName4',
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'unitName4'} className="m-0">
              <InputForm placeholder="Nhập đơn vị cấp 4" size="middle" />
            </Form.Item>
          );
        return value;
      },
    },
    {
      title: 'Nhóm',
      width: 100,
      dataIndex: ['tgroup', 'name'],
      render: (value, record, index) => {
        if (indexHandle === index)
          return (
            <Form.Item name={'groupId'} className="m-0">
              <SelectForm
                options={groupRewardOptions}
                placeholder="Chọn nhóm"
                size="middle"
              />
            </Form.Item>
          );
        return value;
      },
    },
    // {
    //   title: 'Mã đối tượng',
    //   width: 120,
    //   dataIndex: 'objectType',
    //   // objectType: true = 'Trực Tiếp' , false = 'Gián tiếp' (Đối tượng)?
    //   render: (value, record, index) => {
    //     if (indexHandle === index)
    //       return (
    //         <Form.Item name={'objectType'} className="m-0">
    //           <SelectForm placeholder="" size="middle" />
    //         </Form.Item>
    //       );
    //     return value;
    //   },
    // },
    // {
    //   title: 'Đối tượng',
    //   width: 100,
    //   dataIndex: 'objectType',
    //   isShowRender: true,
    //   // render: (value) => (value ? 'Trực tiếp' : 'Gián tiếp'),
    //   render: (value, record, index) => {
    //     if (indexHandle === index)
    //       return (
    //         <Form.Item name={'objectType'} className="m-0">
    //           <SelectForm placeholder="" size="middle" />
    //         </Form.Item>
    //       );
    //     return value ? 'Trực Tiếp' : 'Gián tiếp';
    //   },
    // },
    // {
    //   title: 'Mã trạng thái tính thưởng',
    //   width: 190,
    //   dataIndex: 'rewardStatus',
    //   render: (value, record, index) => {
    //     if (indexHandle === index)
    //       return (
    //         <Form.Item name={'rewardStatus'} className="m-0">
    //           <SelectForm placeholder="" size="middle" />
    //         </Form.Item>
    //       );
    //     return value;
    //   },
    // },
    {
      title: 'Trạng thái tính thưởng',
      width: 170,
      dataIndex: 'rewardStatus',
      isShowRender: true,
      // wardStatus: (true = Tính thưởng , false = Không tính thưởng)
      render: (value, record, index) => {
        if (indexHandle === index) {
          return (
            <Form.Item name={'rewardStatus'} className="m-0">
              <SelectForm
                placeholder="Chọn trạng thái"
                size="middle"
                options={[
                  {
                    label: 'Tính thưởng',
                    value: 'true',
                  },
                  {
                    label: 'Không tính thưởng',
                    value: 'false',
                  },
                ]}
              />
            </Form.Item>
          );
        }
        return value ? 'Tính thưởng' : 'Không tính thưởng';
      },
    },
    {
      title: 'Trạng thái',
      width: 150,
      dataIndex: 'status',
      isShowRender: true,
      // status: true = Hoạt Động , false = Dừng Hoạt Động (Trạng thái)
      render: (value, record, index) => {
        if (indexHandle === index) {
          return (
            <Form.Item name={'status'} className="m-0">
              <SelectForm
                placeholder="Chọn trạng thái"
                size="middle"
                options={[
                  {
                    label: 'Hoạt động',
                    value: 'true',
                  },
                  {
                    label: 'Dừng hoạt động',
                    value: 'false',
                  },
                ]}
              />
            </Form.Item>
          );
        }
        return value ? 'Hoạt Động' : 'Dừng Hoạt Động';
      },
    },
    {
      title: 'Người khai báo',
      width: 140,
      dataIndex: ['tcreateUser', 'fullName'],
    },
    {
      title: 'Ngày khai báo',
      width: 170,
      dataIndex: 'createDate',
      type: 'date',
      render: (text) => formatDate(text),
    },
    {
      title: 'Người cập nhật',
      width: 140,
      dataIndex: ['tmodifyUser', 'fullName'],
    },
    {
      title: 'Ngày cập nhật',
      width: 170,
      dataIndex: 'modifyDate',
      type: 'date',
      render: (text) => formatDate(text),
    },
    {
      fixed: 'right',
      align: 'center',
      width: 70,
      render: (__, record, index) =>
        index === indexHandle ? (
          <Space size={'middle'} className="text-lg">
            <ButtonAction tooltip="Lưu" onClick={onSave}>
              <SaveTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
            <ButtonAction tooltip="Hủy" onClick={onCancel}>
              <CloseCircleTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
          </Space>
        ) : (
          <Space size={'middle'} className="text-lg ">
            <ButtonAction
              tooltip="Chỉnh sửa"
              onClick={() => onEdit(index)}
              disabled={!(user?.isAdmin || user?.isHO || user?.isNS)}
            >
              <EditIcon />
            </ButtonAction>
            <Switch
              disabled={!(user?.isAdmin || user?.isHO || user?.isNS)}
              value={record.status}
              size="small"
              onChange={() =>
                setStatus({
                  id: record.id,
                  status: !record.status,
                })
              }
            />
          </Space>
        ),
    },
  ];

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    refetch(true, columns);
  };
  return (
    <Content>
      <Form form={form}>
        <TableComponent
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={data.items}
          rowKey={(record: any) => record?.id || generateUUID()}
          loading={loading || isPendingUpdateGroupRoleManagementStatus}
          pagination={{
            disabled:
              // isAdd ||
              indexHandle !== -1,
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
      <Modal
        okText="Cập nhật"
        cancelText="Hủy"
        title="Cập nhật trạng thái"
        open={!!status}
        onCancel={() => setStatus(null)}
        onOk={() => {
          mutateUpdateGroupRoleManagementStatus(status);
        }}
        confirmLoading={isPendingUpdateGroupRoleManagementStatus}
      >
        <p>Bạn có chắc chắn muốn cập nhật trạng thái này không?</p>
      </Modal>
    </Content>
  );
});

export default TableGroupRole;
